import { useEffect, useMemo } from 'react';
import type { TaskRecord } from '@shared/types';
import { Icon } from '@renderer/shared/components/icons';
import { StatusBadge } from '@renderer/shared/components/primitives';

function formatDueLabel(locale: string, dueAt: string | null, noDeadlineLabel: string): string {
  if (!dueAt) {
    return noDeadlineLabel;
  }

  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(dueAt));
}

export function TaskFocusModal({
  isOpen,
  locale,
  title,
  task,
  labels,
  statusLabel,
  onClose,
  onEdit,
  onDelete,
  strings
}: {
  isOpen: boolean;
  locale: string;
  title: string;
  task: TaskRecord;
  labels: string[];
  statusLabel: string;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  strings: {
    close: string;
    edit: string;
    delete: string;
    dueAt: string;
    labels: string;
    description: string;
    noDescription: string;
    noDeadline: string;
  };
}): JSX.Element | null {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const dueLabel = useMemo(() => formatDueLabel(locale, task.dueAt, strings.noDeadline), [locale, strings.noDeadline, task.dueAt]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="av-task-modal__overlay"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        aria-modal="true"
        className="av-task-modal"
        role="dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="av-task-modal__header">
          <div className="av-task-modal__title">
            <h2>{title}</h2>
            <StatusBadge label={statusLabel} tone="neutral" />
          </div>
          <button aria-label={strings.close} className="av-task-modal__close" type="button" onClick={onClose}>
            <Icon name="times" />
          </button>
        </div>

        <div className="av-task-modal__body">
          <div className="av-task-modal__row">
            <span className="av-task-modal__label">{strings.dueAt}</span>
            <strong className="av-task-modal__value">{dueLabel}</strong>
          </div>

          <div className="av-task-modal__row">
            <span className="av-task-modal__label">{strings.labels}</span>
            <div className="av-task-modal__tags">
              {labels.length === 0 ? <span className="av-task-modal__muted">—</span> : null}
              {labels.map((label) => (
                <span key={label} className="av-type-pill">
                  {label}
                </span>
              ))}
            </div>
          </div>

          <div className="av-task-modal__row av-task-modal__row--stack">
            <span className="av-task-modal__label">{strings.description}</span>
            {task.description ? (
              <p className="av-task-modal__paragraph">{task.description}</p>
            ) : (
              <p className="av-task-modal__paragraph av-task-modal__muted">{strings.noDescription}</p>
            )}
          </div>
        </div>

        <div className="av-task-modal__footer">
          <button className="av-secondary-button" type="button" onClick={onDelete}>
            <Icon name="trash" />
            <span>{strings.delete}</span>
          </button>
          <button className="av-primary-button" type="button" onClick={onEdit}>
            <Icon name="pencil" />
            <span>{strings.edit}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

